import { useEffect, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import {fetchWithAuth} from '../libs/apiUtils'
import {Meta, Option} from '../types/global'
import {PencilIcon, TrashIcon, UserPlusIcon} from '@heroicons/react/24/outline'
import AddUserModal from '../components/user_management/AddUserModal'
import DeleteUserModal from '../components/user_management/DeleteUserModal'
import EditUserModal from '../components/user_management/EditUserModal'
import Pagination from '../components/Pagination'

interface UserData {
  id: string
  fullName: string
  email: string
  role: string
}

export default function UserManagement() {
  const [users, setUsers] = useState<UserData[]>([])
  const [meta, setMeta] = useState<Meta | null>(null)
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(false)

  const [roleOptions, setRoleOptions] = useState<Option[]>([])

  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [toBeDeletedUser, setToBeDeletedUser] = useState<{id?: string, fullName?: string}>({})

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [toBeEditedUser, setToBeEditedUser] = useState<{id?: string, fullName?: string, email?: string, role?: string}>({})

  const loadUsers = useCallback(async (page = 1) => {
    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page, perPage }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Error fetching users data')
        return
      }

      setUsers(data.data || [])
      setMeta(data.meta)
    } catch (err) {
      toast.error('Network error, check your connection')
    } finally {
      setLoading(false)
    }
  }, [perPage])

  async function loadRoleOptions() {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/roles`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Error fetching roles data')
        return
      }

      setRoleOptions(data.data || [])
    } catch (err) {
      toast.error('Network error, check your connection')
    }
  }

  useEffect(() => {
    loadUsers(page)
    loadRoleOptions()
  }, [page, loadUsers])

  async function handleDeleteUser(id: string) {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/user-management/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data[0].message || 'Error deleting user')
        return
      }

      toast.success('User deleted successfully')
      loadUsers(page) 
    } catch (err) {
      toast.error('Network error, check your connection')
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-extrabold text-neutral-900 tracking-tight">Manajemen User</h2>
          <p className="mt-1 text-sm text-neutral-500">Kelola akses pengguna dan peran mereka di dalam aplikasi.</p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-primary-200 transition-all active:scale-95"
        >
          <UserPlusIcon className="size-5" />
          Tambah User
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-neutral-500 font-medium">Memuat data user...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-20 text-center">
            <div className="bg-primary-50 size-16 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
              <UserPlusIcon className="size-8" />
            </div>
            <h3 className="text-lg font-bold text-neutral-800">Belum Ada User</h3>
            <p className="text-neutral-500 max-w-xs mx-auto mt-2">Silakan klik tombol "Tambah User" untuk mendaftarkan pengguna baru.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neutral-50/50">
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-20">No</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Nama Lengkap</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-32 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {users.map((user, index) => (
                  <tr 
                    key={user.id} 
                    className="group hover:bg-primary-50/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-neutral-500">
                      {(meta?.perPage || 20) * (page - 1) + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-neutral-900 font-semibold">{user.fullName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-neutral-600 font-mono bg-neutral-50 px-2 py-0.5 rounded border border-neutral-100 inline-block">{user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                        user.role.toLowerCase() === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                        user.role.toLowerCase() === 'admin' ? 'bg-primary-100 text-primary-700' : 
                        user.role.toLowerCase() === 'consumer' ? 'bg-indigo-100 text-indigo-700' :
                        'bg-neutral-100 text-neutral-700'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          title="Edit User"
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                          onClick={() => {
                            setToBeEditedUser(user)
                            setIsEditModalOpen(true)
                          }}
                        >
                          <PencilIcon className="size-5" />
                        </button>
                        <button
                          title="Hapus User"
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          onClick={() => {
                            setToBeDeletedUser({id: user.id, fullName: user.fullName})
                            setIsDeleteModalOpen(true)
                          }}
                        >
                          <TrashIcon className="size-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta && (
          <div className="px-6 py-4 bg-neutral-50/50 border-t border-neutral-100">
            <Pagination
              currentPage={page}
              lastPage={meta.lastPage}
              total={meta.total}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>

      <AddUserModal
        isOpen={isAddModalOpen}
        setIsOpen={setIsAddModalOpen}
        roleOptions={roleOptions}
        onSuccess={() => loadUsers(page)}
      />

      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        fullName={toBeDeletedUser.fullName ?? ''}
        onSubmit={() => handleDeleteUser(toBeDeletedUser.id ?? '')}
      />

      <EditUserModal
        user={toBeEditedUser}
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        roleOptions={roleOptions}
        onSuccess={() => loadUsers(page)}
      />
    </div>
  )
}
